import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { QUERY_KEYS } from 'portal/src/api/constants';
import { getRequest } from 'portal/src/api/requestTypes';
import useAuth from 'portal/src/auth/useAuth';
import ContainerLoading from 'portal/src/components/common/ContainerLoading';

import { FormComponentType } from './FormComponentType';

import FormErrorMessage from '../../components/common/forms/FormErrorMessage';

export default function EntityLoader({
  apiPath,
  Component,
  handleBackAction,
  id,
  allowAllOrgs,
}: {
  apiPath: string;
  Component: FormComponentType;
  handleBackAction: () => void;
  id: string;
  allowAllOrgs: boolean;
}) {
  const { getUser, switchOrganization } = useAuth();
  const { organizationId: userOrgId } = getUser();

  const {
    data: entity,
    isError,
    isFetching,
  } = useQuery({
    queryKey: [QUERY_KEYS.detailPageData, id],
    queryFn: async () => {
      return await getRequest(`${apiPath}/${id}`);
    },
    initialData: {},
    refetchOnWindowFocus: false,
  });

  const entityOrgId = entity?.organizationId ?? entity?.id;
  const isInSameOrg = userOrgId === entityOrgId;

  const allowedInOrg = isInSameOrg || allowAllOrgs;

  if (isFetching) {
    return <ContainerLoading height="90vh" additionalLoadingText="form" />;
  }

  if (!isError && allowedInOrg) {
    return <Component entity={entity} handleBackAction={handleBackAction} />;
  }

  if (!isError && !allowedInOrg) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 12,
        }}
      >
        <FormErrorMessage
          actionLabel="Switch organization"
          message="The item you are trying to access is part of another organization"
          handleAction={() => switchOrganization(entity.organizationId)}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 12,
      }}
    >
      <FormErrorMessage
        actionLabel="Search Here"
        message="Sorry, we could not find what you are looking for"
        handleAction={handleBackAction}
      />
    </Box>
  );
}
